<header class="ui-topbar">
  <nav class="ui-topbar__breadcrumb" aria-label="Breadcrumb">
    @if (breadcrumbs().length > 1) {
      <button
        type="button"
        class="ui-topbar__back ui-focus-ring"
        [attr.aria-label]="'topbar.back' | translate"
        (click)="onBackClicked()"
      >
        <span aria-hidden="true" class="material-symbols-rounded">arrow_back</span>
      </button>
    }
    @for (crumb of breadcrumbs(); track crumb.label; let last = $last) {
      @if (crumb.path && !last) {
        <a class="ui-topbar__crumb ui-topbar__crumb--link" [routerLink]="parseCrumbPath(crumb.path)" [queryParams]="parseCrumbQuery(crumb.path)">{{ crumb.label | uiLabelTranslate }}</a>
      } @else {
        <span class="ui-topbar__crumb ui-topbar__crumb--active">{{ crumb.label | uiLabelTranslate }}</span>
      }
      @if (!last) {
        <span class="ui-topbar__crumb-sep" aria-hidden="true">/</span>
      }
    }
  </nav>

  <div class="ui-topbar__actions">
    <button
      type="button"
      class="ui-topbar__icon-btn ui-focus-ring"
      [attr.aria-label]="'topbar.notifications' | translate"
      (click)="toggleNotifications()"
      [attr.aria-expanded]="notificationDrawerOpen()"
      aria-controls="ui-topbar-notification-drawer"
    >
      <span class="material-symbols-rounded" aria-hidden="true">notifications</span>
      @if (unreadNotificationCount() > 0) {
        <span class="ui-topbar__badge" [class.ui-topbar__badge--wide]="unreadNotificationBadge().length > 1">{{ unreadNotificationBadge() }}</span>
      }
    </button>
    
    <div class="ui-topbar__user-menu" (click)="$event.stopPropagation()">
      <button
        type="button"
        class="ui-topbar__user-chip ui-focus-ring"
        (click)="toggleMenu()"
        [attr.aria-label]="'topbar.userMenu' | translate"
        [attr.aria-expanded]="menuOpen()"
        aria-controls="topbar-profile-menu"
      >
        <span class="ui-topbar__avatar" aria-hidden="true">{{ user().initials }}</span>
        <span class="ui-topbar__name">{{ user().name }}</span>
        <span class="material-symbols-rounded" aria-hidden="true" [class.ui-topbar__chevron--open]="menuOpen()">
          keyboard_arrow_down
        </span>
      </button>

      @if (menuOpen()) {
        <section id="topbar-profile-menu" class="ui-topbar__profile-dropdown" role="menu" aria-label="Profile menu">
          <button class="ui-topbar__dropdown-item" type="button" role="menuitem" (click)="onProfileClicked()">
            {{ 'topbar.viewProfile' | translate }}
          </button>
          <div class="ui-topbar__dropdown-language">
            <ui-select
              [label]="'topbar.language' | translate"
              [value]="user().language ?? 'en'"
              [options]="languageOptions"
              (valueChange)="onLanguageChange($event)"
            ></ui-select>
          </div>
          <button class="ui-topbar__dropdown-item ui-topbar__dropdown-item--theme" type="button" role="menuitem" (click)="toggleTheme()">
            <span class="material-symbols-rounded" aria-hidden="true">
              {{ theme() === 'light' ? 'dark_mode' : 'light_mode' }}
            </span>
            <span>{{ (theme() === 'light' ? 'topbar.theme.dark' : 'topbar.theme.light') | translate }}</span>
          </button>
          <button class="ui-topbar__dropdown-item ui-topbar__dropdown-item--danger" type="button" role="menuitem" (click)="onLogoutClicked()">
            {{ 'topbar.logout' | translate }}
          </button>
        </section>
      }
    </div>
  </div>
</header>

<section class="ui-topbar-drawer-layer" [class.ui-topbar-drawer-layer--open]="notificationDrawerOpen()">
  <button type="button" class="ui-topbar-drawer-layer__backdrop" [attr.aria-label]="'topbar.closeNotifications' | translate" (click)="closeNotifications()"></button>
  <aside
    id="ui-topbar-notification-drawer"
    class="ui-topbar-drawer"
    [class.ui-topbar-drawer--open]="notificationDrawerOpen()"
    aria-live="polite"
    (click)="$event.stopPropagation()"
  >
    <header class="ui-topbar-drawer__header">
      <div>
        <h3>{{ 'topbar.notifications' | translate }}</h3>
        <span>{{ 'topbar.unreadCount' | translate: { count: unreadNotificationCount() } }}</span>
      </div>
      <div class="ui-topbar-drawer__actions">
        <ui-button variant="ghost" size="sm" [disabled]="!unreadNotificationCount()" [ariaLabel]="'topbar.markAllAsRead' | translate" (click)="markAllAsRead()">
          {{ 'topbar.markAllAsRead' | translate }}
        </ui-button>
        <ui-button variant="danger" size="sm" [disabled]="!notifications().length" [ariaLabel]="'topbar.clearNotifications' | translate" (click)="clearNotifications()">
          {{ 'topbar.clearNotifications' | translate }}
        </ui-button>
      </div>
    </header>
    <div class="ui-topbar-drawer__tabs" role="tablist" aria-label="Notification filters">
      <button
        type="button"
        role="tab"
        [class.ui-topbar-drawer__tab--active]="activeNotificationTab() === 'all'"
        [attr.aria-selected]="activeNotificationTab() === 'all'"
        (click)="setNotificationTab('all')"
      >
        {{ 'topbar.allNotifications' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        [class.ui-topbar-drawer__tab--active]="activeNotificationTab() === 'unread'"
        [attr.aria-selected]="activeNotificationTab() === 'unread'"
        (click)="setNotificationTab('unread')"
      >
        {{ 'topbar.unreadNotifications' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        [class.ui-topbar-drawer__tab--active]="activeNotificationTab() === 'read'"
        [attr.aria-selected]="activeNotificationTab() === 'read'"
        (click)="setNotificationTab('read')"
      >
        {{ 'topbar.readNotifications' | translate }}
      </button>
    </div>
    <div class="ui-topbar-drawer__body">
      @if (!filteredNotifications().length) {
        <div class="ui-topbar-empty">
          <strong>{{ 'topbar.noNotifications' | translate }}</strong>
          <span>{{ 'topbar.noNotificationsHint' | translate }}</span>
        </div>
      }
      @for (item of visibleNotifications(); track item.id) {
        <ui-card
          class="ui-topbar-notification"
          [class.ui-topbar-notification--read]="item.read"
          [class.ui-topbar-notification--unread]="!item.read"
          [attr.data-type]="item.type"
        >
          <button
            type="button"
            class="ui-topbar-notification__summary"
            [attr.aria-expanded]="item.expanded"
            (click)="toggleNotification(item.id)"
          >
            <span class="ui-topbar-notification__marker" aria-hidden="true"></span>
            <span class="ui-topbar-notification__copy">
              <strong>{{ item.title | uiLabelTranslate }}</strong>
            </span>
            <span class="material-symbols-rounded ui-topbar-notification__chevron" [class.ui-topbar-notification__chevron--open]="item.expanded" aria-hidden="true">
              expand_more
            </span>
          </button>
          @if (item.expanded) {
            <div class="ui-topbar-notification__details">
              <small>{{ item.meta | uiLabelTranslate }}</small>
              <p>{{ item.description | uiLabelTranslate }}</p>
            </div>
          }
        </ui-card>
      }
      @if (hasMoreNotifications()) {
        <div class="ui-topbar-loading">
          <ui-button variant="secondary" size="sm" [ariaLabel]="'topbar.loadMore' | translate" (click)="loadMoreNotifications($event)">
            <span class="ui-topbar-loading__label">
              <span class="material-symbols-rounded" aria-hidden="true">unfold_more</span>
              {{ 'topbar.loadMore' | translate }}
            </span>
          </ui-button>
        </div>
      }
    </div>
  </aside>
</section>
